import { useGetUserFullNameWithFallback } from '@pba/react-utils';
import type { ChatUser, LabelInValue } from 'models';
import { type ReactNode, useCallback } from 'react';
import { EntitySnippet } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependencies
import { useGetNodeUserAvatar } from 'features/users/hooks';
import { useGetListUserDisplayName } from '../../../hooks';

export type ChatCreateUserOption = {
  email: string;
  firstName: string;
  lastName: string;
  optionLabel: ReactNode;
} & LabelInValue;

const SELECTED_AVATAR_SIZE = 12;

export const useChatCreateUserSelectCreateOption = () => {
  const getAvatarUrl = useGetNodeUserAvatar();
  const getUserFullNameWithFallback = useGetUserFullNameWithFallback();
  const getListUserDisplayName = useGetListUserDisplayName();

  return useCallback(
    (user: ChatUser): ChatCreateUserOption => {
      const fullName = getUserFullNameWithFallback(user);
      const name = getListUserDisplayName(user.id, fullName);
      const avatarProperties = {
        src: getAvatarUrl({ userId: user.id }),
      };

      return {
        email: user.email,
        firstName: user.firstName,
        label: (
          <EntitySnippet
            avatarProps={{ ...avatarProperties, size: SELECTED_AVATAR_SIZE }}
            titleProps={{ children: name, size: 'small' }}
          />
        ),
        lastName: user.lastName,
        optionLabel: (
          <EntitySnippet
            avatarProps={{ ...avatarProperties, size: 'sm' }}
            descriptionProps={{
              children: user.email,
              ellipsis: { tooltip: false },
            }}
            titleProps={{ children: name }}
          />
        ),
        value: user.id,
      };
    },
    [getAvatarUrl, getListUserDisplayName, getUserFullNameWithFallback],
  );
};
